import { convertValueFromInches, convertValueToInches } from '../../../../../utils/unitUtils';
import { formatHeatInputLabel, isWeldingHeatParameterName } from '../../../../../utils/weldParameterLabels';

export const STATUS_CONFIG_UNIT_OPTIONS = {
  wire_speed: ['in/min', 'cm/min', 'mm/min'],
  travel_speed: ['in/min', 'cm/min', 'mm/min'],
  oscillation_width: ['in', 'cm', 'mm'],
  heat: ['kJ/in', 'kJ/cm', 'kJ/mm'],
  true_energy: ['kJ/in', 'kJ/cm', 'kJ/mm'],
} as const;

export type StatusConfigParameterKey = keyof typeof STATUS_CONFIG_UNIT_OPTIONS;

const BASE_UNITS: Record<StatusConfigParameterKey, string> = {
  wire_speed: 'in/min',
  travel_speed: 'in/min',
  oscillation_width: 'in',
  heat: 'kJ/in',
  true_energy: 'kJ/in',
};

const SPEED_KEYS: StatusConfigParameterKey[] = ['wire_speed', 'travel_speed'];
const LENGTH_KEYS: StatusConfigParameterKey[] = ['oscillation_width'];
const ENERGY_KEYS: StatusConfigParameterKey[] = ['heat', 'true_energy'];

const round4 = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return Math.round(value * 10000) / 10000;
};

export const isStatusConfigUnitParameter = (
  paramKey: string | null
): paramKey is StatusConfigParameterKey =>
  !!paramKey && paramKey in STATUS_CONFIG_UNIT_OPTIONS;

const isValidUnit = (key: StatusConfigParameterKey, unit: string): boolean =>
  (STATUS_CONFIG_UNIT_OPTIONS[key] as readonly string[]).includes(unit);

const convertSpeedFromBase = (value: number, toUnit: string): number => {
  switch (toUnit) {
    case 'in/min': return round4(value);
    case 'cm/min': return round4(value * 2.54);
    case 'mm/min': return round4(value * 25.4);
    default: return round4(value);
  }
};

const convertSpeedToBase = (value: number, fromUnit: string): number => {
  switch (fromUnit) {
    case 'in/min': return round4(value);
    case 'cm/min': return round4(value / 2.54);
    case 'mm/min': return round4(value / 25.4);
    default: return round4(value);
  }
};

const convertEnergyFromBase = (value: number, toUnit: string): number => {
  switch (toUnit) {
    case 'kJ/in': return round4(value);
    case 'kJ/cm': return round4(value / 2.54);
    case 'kJ/mm': return round4(value / 25.4);
    default: return round4(value);
  }
};

const convertEnergyToBase = (value: number, fromUnit: string): number => {
  switch (fromUnit) {
    case 'kJ/in': return round4(value);
    case 'kJ/cm': return round4(value * 2.54);
    case 'kJ/mm': return round4(value * 25.4);
    default: return round4(value);
  }
};

export const convertStatusConfigFromBase = (
  value: number,
  paramKey: StatusConfigParameterKey,
  displayUnit: string
): number => {
  if (SPEED_KEYS.includes(paramKey)) return convertSpeedFromBase(value, displayUnit);
  if (LENGTH_KEYS.includes(paramKey)) return convertValueFromInches(value, displayUnit);
  if (ENERGY_KEYS.includes(paramKey)) return convertEnergyFromBase(value, displayUnit);
  return value;
};

export const convertStatusConfigToBase = (
  value: number,
  paramKey: StatusConfigParameterKey,
  displayUnit: string
): number => {
  if (SPEED_KEYS.includes(paramKey)) return convertSpeedToBase(value, displayUnit);
  if (LENGTH_KEYS.includes(paramKey)) return convertValueToInches(value, displayUnit);
  if (ENERGY_KEYS.includes(paramKey)) return convertEnergyToBase(value, displayUnit);
  return value;
};

export const getDefaultStatusConfigUnits = (
  projectUnit = 'in'
): Record<StatusConfigParameterKey, string> => {
  const normalized = projectUnit === 'mm' || projectUnit === 'cm' ? projectUnit : 'in';
  return {
    wire_speed: `${normalized}/min`,
    travel_speed: `${normalized}/min`,
    oscillation_width: normalized,
    heat: `kJ/${normalized}`,
    true_energy: `kJ/${normalized}`,
  };
};

export const extractStatusConfigUnits = (
  apiUnits?: Partial<Record<string, string>>,
  projectUnit = 'in'
): Record<StatusConfigParameterKey, string> => {
  const defaults = getDefaultStatusConfigUnits(projectUnit);
  return (Object.keys(STATUS_CONFIG_UNIT_OPTIONS) as StatusConfigParameterKey[]).reduce(
    (acc, key) => {
      const candidate = apiUnits?.[key] || defaults[key];
      acc[key] = candidate && isValidUnit(key, candidate) ? candidate : BASE_UNITS[key];
      return acc;
    },
    {} as Record<StatusConfigParameterKey, string>
  );
};

export const getStatusConfigParameterLabel = (
  parameterName: string,
  units: Record<StatusConfigParameterKey, string>
): string => {
  if (parameterName.includes('Current')) return 'Current (A)';
  if (parameterName.includes('Volts')) return 'Volts (V)';
  if (parameterName.includes('Wire Speed')) return `Wire Speed (${units.wire_speed})`;
  if (parameterName.includes('Travel Speed')) return `Travel Speed (${units.travel_speed})`;
  if (parameterName.includes('Oscillation Width')) return `Oscillation Width (${units.oscillation_width})`;
  if (parameterName.includes('True Energy')) return `True Energy (${units.true_energy})`;
  if (isWeldingHeatParameterName(parameterName)) return formatHeatInputLabel(units.heat);
  return parameterName;
};

export const getStatusConfigParameterUnit = (
  parameterName: string,
  units: Record<StatusConfigParameterKey, string>
): string => {
  if (parameterName.includes('Current')) return 'A';
  if (parameterName.includes('Volts')) return 'V';
  if (parameterName.includes('Wire Speed')) return units.wire_speed;
  if (parameterName.includes('Travel Speed')) return units.travel_speed;
  if (parameterName.includes('Oscillation Width')) return units.oscillation_width;
  if (parameterName.includes('Heat')) return units.heat;
  if (parameterName.includes('True Energy')) return units.true_energy;
  return '';
};

export const formatDisplayDecimals = (value: number, parameter: string): string => {
  if (typeof value !== 'number' || isNaN(value)) return '';
  if (parameter.includes('Current') || parameter.includes('Volts')) return value.toFixed(1);
  if (parameter.includes('Wire Speed') || parameter.includes('Travel Speed')) return value.toFixed(2);
  if (parameter.includes('Oscillation Width')) return value.toFixed(3);
  if (parameter.includes('Heat') || parameter.includes('True Energy')) return value.toFixed(2);
  return value.toFixed(2);
};
